// The spell page control. A page is bought exactly like a sword, except that it is worth
// nothing until it names the spell written on it, and a spell takes at most one page.
// Nothing here loads a spell library. The list on the sheet is free to read and is exactly
// the set a page can do anything for.

import type { PanelOwned, PanelSpell } from "../vendor/web";
import type { CartLine } from "./cart";
import type { BakedRow } from "./catalogue";
import { stallFor, type Stall } from "./quality";

const UNRESOLVED = /@([^@]+)@/;

/** The placeholder key the description carries. GCS builds the `@` pair itself, so the key
 *  is written bare into `replacements`. */
export function placeholder(row: BakedRow): string | null {
  const found = UNRESOLVED.exec(row.description);
  return found?.[1] ?? null;
}

/** What a page adds to the one spell it names, read off the `spell_bonus` feature. */
export function bonus(row: BakedRow): number {
  const features = row["features"];
  if (!Array.isArray(features)) return 0;
  for (const feature of features as { type?: unknown; amount?: unknown }[]) {
    if (feature.type === "spell_bonus" && typeof feature.amount === "number") {
      return feature.amount;
    }
  }
  return 0;
}

/** The row's name with the chosen spell written in, which is how GCS prints it. Before a
 *  spell is chosen the placeholder stands, and reads as the blank it is. */
export function resolvedName(row: BakedRow, spell: string | undefined): string {
  const key = placeholder(row);
  if (key === null || spell === undefined) return row.description;
  return row.description.split(`@${key}@`).join(spell);
}

/** Every spell that already carries a page, and what that page adds: the ones on the
 *  arriving sheet, and the ones the cart is buying. Duplicates stack in GCS, so two cheap
 *  pages would buy the bonus of one page costing twelve times as much. */
export function paged(
  owned: readonly PanelOwned[],
  lines: readonly CartLine[],
  stalls: Stall[],
): Map<string, number> {
  const held = new Map<string, number>();
  const page = (item: string): BakedRow | null => {
    const stall = stallFor(stalls, item);
    if (stall === undefined || placeholder(stall.stock) === null) return null;
    return stall.stock;
  };

  for (const row of owned) {
    const arrived = row.catalogue_id === undefined ? null : page(row.catalogue_id);
    if (arrived === null || row.replacement === undefined) continue;
    held.set(row.replacement, bonus(arrived));
  }
  for (const line of lines) {
    const bought = page(line.item);
    if (bought === null || line.spell === undefined) continue;
    held.set(line.spell, bonus(bought));
  }
  return held;
}

/** One spell as the select offers it. */
export interface Choice {
  name: string;
  /** The level on the arriving sheet, which is the only place this number may come from. */
  before: number;
  after: number;
  /** The page this spell already carries, which is why it can take no other. */
  held: number | null;
}

/** Every spell one page row offers, in the order the sheet lists them. */
export function choices(
  spells: readonly PanelSpell[],
  row: BakedRow,
  held: Map<string, number>,
): Choice[] {
  const amount = bonus(row);
  return spells.map((spell) => ({
    name: spell.name,
    before: spell.before,
    after: spell.before + amount,
    held: held.get(spell.name) ?? null,
  }));
}

export function choiceLabel(choice: Choice): string {
  if (choice.held !== null) return `${choice.name} - has a +${choice.held} page`;
  return `${choice.name} ${choice.before} -> ${choice.after}`;
}
